import type { SystemVersionDTO } from "@repo/codegen/teashop-base-api"
import type * as Login from "./types/login"
import type { CallerHandledAuthFailureOptions } from "@/utils/callerHandledAuthFailure"
import { Base64 } from "js-base64"
import { API_GRAY_HEADER } from "@/utils/apiGrayHeader"
import { getCallerHandledAuthFailureConfig } from "@/utils/callerHandledAuthFailure"
import { request } from "@/utils/service"

export type OAuthClient = "teashop" | "teashop_merchant"

interface LoginApiOptions extends CallerHandledAuthFailureOptions {
  client?: OAuthClient
}

interface BusinessContextRequestOptions extends CallerHandledAuthFailureOptions {
  businessContextSystemVersion?: SystemVersionDTO
}

const OAUTH_CLIENT_SECRET: Record<OAuthClient, string> = {
  teashop: "teashop",
  teashop_merchant: "teashop_merchant",
}

const localVersionHeaders = () => {
  const zodanceVersion = import.meta.env.VITE_ZODANCE_VERSION
  return zodanceVersion
    ? {
        "ZODANCE-VERSION": zodanceVersion,
      }
    : {}
}

const isScopeAuthCode = (code: string) => code.startsWith("SCOPE@")

/** 登录并返回 Token */
export function loginApi(code: string, options: LoginApiOptions = {}) {
  const client = options.client ?? "teashop"
  return request<Login.LoginUserInfo>({
    url: `/auth/oauth/token?grant_type=app&code=${encodeURIComponent(code)}`,
    method: "post",
    ...getCallerHandledAuthFailureConfig(options),
    headers: {
      "Tenant-Id": "teashop",
      addIfNull: "N",
      Authorization: `Basic ${Base64.encode(`${client}:${OAUTH_CLIENT_SECRET[client]}`)}`,
      ...(isScopeAuthCode(code) ? { [API_GRAY_HEADER]: "true" } : {}),
      ...localVersionHeaders(),
    },
  })
}

/** 微信登录 */
export function wxLoginApi(code: string) {
  return request<Login.LoginUserInfo>({
    url: `/auth/oauth/token?grant_type=app&code=${encodeURIComponent(`WX_SCAN@wx530773d075ed826a@${code}@merchant`)}`,
    method: "post",
    headers: {
      "Tenant-Id": "teashop",
      Authorization: `Basic ${Base64.encode("teashop:teashop")}`,
      addIfNull: "Y",
      ...localVersionHeaders(),
    },
  })
}

interface RefreshTokenApiOptions {
  client?: OAuthClient
}

/** 刷新token */
export function refreshTokenApi(scope: string, refreshToken: string, options: RefreshTokenApiOptions = {}) {
  const client = options.client ?? "teashop"
  const req = request<Login.LoginUserInfo>({
    url: `/auth/oauth/token?grant_type=refresh_token`,
    method: "post",
    skipBusinessContextBarrier: true,
    params: {
      scope,
      refresh_token: refreshToken,
    },
    headers: {
      "Tenant-Id": "teashop",
      Authorization: `Basic ${Base64.encode(`${client}:${OAUTH_CLIENT_SECRET[client]}`)}`,
      "A-Request-Date": new Date().toISOString(),
      ...localVersionHeaders(),
    },
  })
  return req
}

/**
 * 登出
 */
export function logoutApi(authTokenOverride?: string) {
  return request({
    url: `/auth/token/logout`,
    method: "DELETE",
    skipBusinessContextBarrier: true,
    ignoreBusinessContextStaleResponse: true,
    ...(authTokenOverride === undefined ? {} : { authTokenOverride }),
  })
}

/** 获取用户权限角色信息 */
export function getUserRolesApi(
  options: CallerHandledAuthFailureOptions & BusinessContextRequestOptions = {},
) {
  return request<Login.RolesInfoResponseData>({
    url: `/teashop_merchant/merchant_user/role_info`,
    method: "get",
    businessContextSystemVersion: options.businessContextSystemVersion,
    ...(options.skipBusinessContextBarrier ? { skipBusinessContextBarrier: true } : {}),
    ...getCallerHandledAuthFailureConfig(options),
  })
}

/** 获取店铺详情 */
export function getShopInfoApi(shopId: number, options: BusinessContextRequestOptions = {}) {
  return request<Login.ShopInfoResponseData>({
    url: `/teashop_merchant/merchant_shop/${shopId}`,
    method: "get",
    businessContextSystemVersion: options.businessContextSystemVersion,
    ...getCallerHandledAuthFailureConfig(options),
  })
}

/** 更新店铺信息 */
export function updateShopInfo(data: Login.ShopInfoType) {
  return request<Login.ShopInfoResponseData>({
    url: `/teashop_merchant/merchant_shop`,
    method: "put",
    data,
  })
}

/** 修改店铺状态 */
export function updateShopStatus(shopId: number, status: number) {
  return request<Login.ShopInfoResponseData>({
    url: `/teashop_merchant/merchant_shop/update_status/${shopId}/${status}`,
    method: "post",
  })
}

/** 门店标识、环境图和资质图片，沿用商户图片上传接口。 */
export function uploadShopProfileImage(file: File) {
  const data = new FormData()
  data.append("file", file)
  return request<IApiResponseData<string>>({
    url: "/teashop_merchant/merchant/pms/item/upload",
    method: "POST",
    data,
    headers: { "Content-Type": "multipart/form-data" },
    timeout: 120000,
  })
}

/** 发送短信验证码 */
export function sendSmsCodeApi(phone: string) {
  return request<IApiResponseData<boolean>>({
    url: `/teashop_base/social/send_login_code?phone=${phone}`,
    method: "post",
  })
}

/** 修改打印设置 */
export function updatePrintSetting(printSetting: string) {
  return request<IApiResponseData<boolean>>({
    url: `/teashop_merchant/merchant_shop/update_print_setting`,
    method: "post",
    params: {
      printSetting,
    },
  })
}
